import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FilterGroup from "@/components/feature/FilterGroup";
import ShortFilmCard from "@/components/feature/ShortFilmCard";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  filterFilms,
  shortFilms,
  type DateRange,
  type DurationBucket,
  type FilmSort,
} from "@/lib/content";
import { filmTypes, type FilmType } from "@/mocks/shortFilms";
import { countFilmsByType } from "@/lib/content";

const PAGE_STEP = 6;

type TypeFilter = FilmType | "all";

export default function ShortFilmCategoryPage() {
  useDocumentMeta(
    "短片分类 - 小电影影视短片推荐",
    "浏览丰富影视短片资源，支持多维度筛选与排序，涵盖剧情、动画、纪录片、科幻与喜剧短片，快速找到符合口味的好作品。",
    "短片分类,影视短片,剧情短片,动画短片,纪录片短片,科幻短片,小电影",
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get("type");
  const initialType: TypeFilter =
    typeParam && (filmTypes as string[]).includes(typeParam) ? (typeParam as FilmType) : "all";

  const [type, setType] = useState<TypeFilter>(initialType);
  const [duration, setDuration] = useState<DurationBucket>("all");
  const [range, setRange] = useState<DateRange>("all");
  const [sort, setSort] = useState<FilmSort>("latest");
  const [visible, setVisible] = useState(PAGE_STEP);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    const next = searchParams.get("type");
    setType(next && (filmTypes as string[]).includes(next) ? (next as FilmType) : "all");
    setVisible(PAGE_STEP);
  }, [searchParams]);

  const films = filterFilms({ type, duration, range, sort });
  const shown = films.slice(0, visible);

  const handleTypeChange = (value: string) => {
    const next = value as TypeFilter;
    setType(next);
    setVisible(PAGE_STEP);
    if (next === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ type: next });
    }
  };

  const handleDurationChange = (value: string) => {
    setDuration(value as DurationBucket);
    setVisible(PAGE_STEP);
  };

  const handleRangeChange = (value: string) => {
    setRange(value as DateRange);
    setVisible(PAGE_STEP);
  };

  const handleSortChange = (value: FilmSort) => {
    setSort(value);
    setVisible(PAGE_STEP);
  };

  const resetFilters = () => {
    setType("all");
    setDuration("all");
    setRange("all");
    setSort("latest");
    setVisible(PAGE_STEP);
    setSearchParams({});
    setSheetOpen(false);
  };

  const activeFilterCount =
    (type !== "all" ? 1 : 0) + (duration !== "all" ? 1 : 0) + (range !== "all" ? 1 : 0);

  const filterPanel = (
    <>
      <FilterGroup
        label="类型"
        value={type}
        onChange={handleTypeChange}
        options={[
          { value: "all", label: "全部", count: shortFilms.length },
          ...filmTypes.map((item) => ({
            value: item,
            label: item,
            count: countFilmsByType(item),
          })),
        ]}
      />
      <div className="h-px bg-background-200" />
      <FilterGroup
        label="时长"
        value={duration}
        onChange={handleDurationChange}
        options={[
          { value: "all", label: "全部时长" },
          { value: "short", label: "0-5 分钟" },
          { value: "medium", label: "5-15 分钟" },
          { value: "long", label: "15 分钟以上" },
        ]}
      />
      <div className="h-px bg-background-200" />
      <FilterGroup
        label="发布时间"
        value={range}
        onChange={handleRangeChange}
        options={[
          { value: "all", label: "全部时间" },
          { value: "1m", label: "最近一个月" },
          { value: "3m", label: "最近三个月" },
        ]}
      />
    </>
  );

  const sortOptions: { value: FilmSort; label: string; icon: string }[] = [
    { value: "latest", label: "最新发布", icon: "ri-time-line" },
    { value: "popular", label: "按热度", icon: "ri-fire-line" },
    { value: "duration", label: "按时长", icon: "ri-timer-line" },
  ];

  return (
    <PageShell>
      <section className="bg-background-100/70 border-b border-background-200">
        <div className="container-page py-6 md:py-8">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "短片分类" }]} />
          <div className="mt-3 flex flex-col md:flex-row md:items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-foreground-950">
                短片分类
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                丰富影视短片，按类型、时长与发布时间筛选，找到此刻最想看的那一部。
              </p>
            </div>
            <p className="text-xs text-foreground-500">
              共 <span className="font-label font-bold text-primary-600">{films.length}</span> 部符合条件
            </p>
          </div>
        </div>
      </section>

      <div className="container-page py-6 md:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <aside className="hidden lg:block w-[268px] shrink-0">
            <div className="sticky top-[126px] rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between pb-4">
                <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                  <i className="ri-filter-3-line text-primary-600" />
                  筛选条件
                </h2>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-[12px] text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  重置
                </button>
              </div>
              <div className="space-y-0">{filterPanel}</div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSheetOpen(true)}
                  className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-background-300 bg-background-50 text-[13px] text-foreground-800 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-filter-3-line" />
                  筛选
                  {activeFilterCount > 0 && (
                    <span className="ml-0.5 w-5 h-5 rounded-full bg-primary-500 text-background-50 text-[11px] flex items-center justify-center font-label">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <div className="hidden sm:flex items-center gap-1 p-1 rounded-full bg-background-100">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSortChange(option.value)}
                      className={`inline-flex items-center gap-1 h-7 px-3 rounded-full text-[12px] transition-colors cursor-pointer whitespace-nowrap ${
                        sort === option.value
                          ? "bg-background-50 text-primary-600 font-medium shadow-none"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      <i className={`${option.icon} text-[13px]`} />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <select
                aria-label="排序方式"
                value={sort}
                onChange={(event) => handleSortChange(event.target.value as FilmSort)}
                className="sm:hidden h-9 px-2.5 rounded-md border border-background-300 bg-background-50 text-[13px] text-foreground-800 cursor-pointer"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {activeFilterCount > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-[12px] text-foreground-500">已选：</span>
                {type !== "all" && (
                  <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-primary-50 text-primary-700 text-[12px]">
                    {type}
                    <button
                      type="button"
                      aria-label={`移除${type}筛选`}
                      onClick={() => handleTypeChange("all")}
                      className="cursor-pointer hover:text-primary-900"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </span>
                )}
                {duration !== "all" && (
                  <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-secondary-100 text-secondary-900 text-[12px]">
                    {duration === "short" ? "0-5 分钟" : duration === "medium" ? "5-15 分钟" : "15 分钟以上"}
                    <button
                      type="button"
                      aria-label="移除时长筛选"
                      onClick={() => handleDurationChange("all")}
                      className="cursor-pointer hover:text-secondary-950"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </span>
                )}
                {range !== "all" && (
                  <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-accent-100 text-accent-900 text-[12px]">
                    {range === "1m" ? "最近一个月" : "最近三个月"}
                    <button
                      type="button"
                      aria-label="移除时间筛选"
                      onClick={() => handleRangeChange("all")}
                      className="cursor-pointer hover:text-accent-950"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </span>
                )}
              </div>
            )}

            {shown.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
                  {shown.map((film) => (
                    <ShortFilmCard key={film.id} film={film} />
                  ))}
                </div>

                <div className="mt-8 flex flex-col items-center gap-3">
                  <p className="text-xs text-foreground-500">
                    已展示 {shown.length} / {films.length} 部短片
                  </p>
                  {visible < films.length ? (
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => Math.min(prev + PAGE_STEP, films.length))}
                      className="inline-flex items-center gap-2 h-10 px-6 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line" />
                      加载更多短片
                    </button>
                  ) : (
                    <p className="text-xs text-foreground-400">已经到底了，去首页看看其他推荐吧</p>
                  )}
                </div>
              </>
            ) : (
              <EmptyState
                title="没有找到符合条件的短片"
                description="试着放宽时长或发布时间，也可以直接浏览全部短片。"
                actionLabel="重置筛选"
                onAction={resetFilters}
                icon="ri-film-line"
              />
            )}
          </div>
        </div>
      </div>

      {sheetOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex items-end bg-foreground-950/60 animate-fade-in"
          role="dialog"
          aria-modal="true"
          aria-label="筛选面板"
          onClick={() => setSheetOpen(false)}
        >
          <div
            className="w-full bg-background-50 rounded-t-lg p-5 animate-slide-up max-h-[82vh] overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-4 border-b border-background-200">
              <h2 className="text-base font-bold text-foreground-950">筛选短片</h2>
              <button
                type="button"
                aria-label="关闭筛选面板"
                onClick={() => setSheetOpen(false)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="py-2">{filterPanel}</div>
            <div className="flex gap-3 pt-4 border-t border-background-200">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 h-10 rounded-md border border-background-300 text-sm text-foreground-800 cursor-pointer whitespace-nowrap"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex-1 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                查看 {films.length} 部结果
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}